import { LuIcon, Menu } from "@overmap-ai/blocks"
import { EMPTY_ARRAY } from "@overmap-ai/common"
import { fieldDataMapping, type MultiStringField } from "@overmap-ai/forms"
import { useOpenState } from "@overmap-ai/hooks"
import { use, useCallback } from "react"

import { MultiStringDialog } from "../../../components/Dialogs/MultiStringDialog"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { MultiStringFieldFilterDef } from "./MultiStringFieldFilterDef"

interface MultiStringFieldFilterCellProps extends FilterDefRenderProps<MultiStringFieldFilterDef> {
	field: MultiStringField
}

export const MultiStringFieldFilterCell = (props: MultiStringFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const [showTextInputDialog, openTextInputDialog, closeTextInputDialog] = useOpenState(false)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const handleSelectEmpty = useCallback(() => {
		filter.setFilterValue(null)
	}, [filter])

	const getFilterValueRender = () => {
		if (filterValue === undefined) return "..."

		if (filterValue === null) return "empty"

		switch (filterValue.length) {
			case 0:
				return "..."
			case 1: {
				const value = filterValue[0]
				return value.toString()
			}
			default:
				return `${filterValue.length} values`
		}
	}

	return (
		<>
			<FilterCellRoot>
				<FilterCellBadge className="shrink-1 min-w-0">
					<LuIcon icon={fieldDataMapping[field.type].icon} />
					<span className="truncate">{field.label}</span>
				</FilterCellBadge>

				<FilterModifierDropdown filterDef={filterDef} filter={filter}>
					<FilterCellButton>
						{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
					</FilterCellButton>
				</FilterModifierDropdown>

				<Menu.Root>
					<Menu.ClickTrigger>
						<FilterCellButton className="truncate">{getFilterValueRender()}</FilterCellButton>
					</Menu.ClickTrigger>
					<Menu.Content size={size}>
						<Menu.Item onSelect={openTextInputDialog}>Custom values</Menu.Item>
						<Menu.Item onSelect={handleSelectEmpty}>empty</Menu.Item>
					</Menu.Content>
				</Menu.Root>

				<RemoveFilterButton filter={filter} filterManager={filterManager} />
			</FilterCellRoot>
			<MultiStringDialog
				title="Custom value"
				open={showTextInputDialog}
				initialValues={filterValue ?? EMPTY_ARRAY}
				onOpenChange={closeTextInputDialog}
				onSubmit={filter.setFilterValue}
				size="sm"
			/>
		</>
	)
}
